<script setup>
import { computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import ErrorLayout from '../Layouts/ErrorLayout.vue';
import { useTitle as pageTitle } from '@/Composables/title.js';

defineOptions({ layout: ErrorLayout });

const props = defineProps({
    status: {
        type: Number,
        required: true,
    },
    locale: {
        type: String,
        required: true,
    },
});

const title = computed(() => ({
    503: {
        en: 'Service Unavailable',
        de: 'Dienst nicht verfügbar',
    },
    500: {
        en: 'Server Error',
        de: 'Server Error',
    },
    404: {
        en: 'Page Not Found',
        de: 'Seite nicht gefunden',
    },
    403: {
        en: 'Forbidden',
        de: 'Verboten',
    },
}[props.status]));

const description = computed(() => ({
    503: {
        en: 'Sorry, we are doing some maintenance. Please check back soon.',
        de: 'Entschuldigung, wir führen einige Wartungsarbeiten durch. Bitte schauen Sie bald wieder vorbei.',
    },
    500: {
        en: 'Whoops, something went wrong on our servers.',
        de: 'Ups, auf unseren Servern ist etwas schief gelaufen.',
    },
    404: {
        en: 'Sorry, the page you are looking for could not be found.',
        de: 'Der von Ihnen gewünschte Inhalt steht leider nicht zur Verfügung.',
    },
    403: {
        en: 'Sorry, you are forbidden from accessing this page.',
        de: 'Entschuldigung, der Zugriff auf diese Seite ist Ihnen nicht gestattet.',
    },
}[props.status]));

const button = {
    en: 'Go home',
    de: 'Zur Startseite',
};

const page = usePage();
const homeUrl = computed(() => page.props.homeUrl);

const lang = props.locale === 'de' ? 'de' : 'en';
</script>

<template>
    <Head :title="pageTitle(title[lang])" />

    <div class="container px-4 py-8 md:py-16 mx-auto">
        <div class="w-full sm:max-w-xl bg-white shadow-lg sm:rounded-lg px-4 sm:px-8 py-4 sm:py-6 mx-auto space-y-4">
            <div class="text-center space-y-4">
                <h2 class="font-semibold text-[4em]/none sm:text-[5em]/none text-sunset-orange-500 purple-theme:text-purple-700">
                    {{ status }}
                </h2>

                <div>
                    <h1 class="font-semibold text-lg sm:text-xl">
                        {{ title[lang] }}
                    </h1>

                    <p>
                        {{ description[lang] }}
                    </p>
                </div>
            </div>

            <div
                v-if="homeUrl"
                class="text-center"
            >
                <Link
                    :href="homeUrl"
                    class="inline-block font-bold text-base text-white purple-theme:text-purple-700 bg-deep-teal-700 purple-theme:bg-golden-fizz-500 border-2 border-deep-teal-700 purple-theme:border-golden-fizz-500 rounded-3xl hover:bg-deep-teal-900 purple-theme:hover:bg-golden-fizz-600 hover:border-deep-teal-900 purple-theme:hover:border-golden-fizz-600 focus:outline-none focus:bg-deep-teal-900 purple-theme:focus:bg-golden-fizz-600 focus:border-deep-teal-900 purple-theme:focus:border-golden-fizz-600 px-5 py-1.5 transition-colors"
                >
                    {{ button[lang] }}
                </Link>
            </div>
        </div>
    </div>
</template>
